import React from "react";
import { CButton } from "@coreui/react";
import { SectionCard } from "../__sections";
import { formatBytes } from "../../../helpers/general";

export interface GalleryItem {
  id: string;
  url: string;
  type: "IMAGE" | "VIDEO";
  isExisting: boolean;
  file?: File;
  order: number;
  status: "uploading" | "error" | "done";
}

interface Step7Props {
  galleryItems: GalleryItem[];
  isDragOver: boolean;
  setIsDragOver: (v: boolean) => void;
  draggedIndex: number | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  addFiles: (files: FileList | File[]) => void;
  removeFile: (idx: number) => void;
  clearAll: () => void;
  handleDrop: (e: React.DragEvent) => void;
  handleItemDragStart: (index: number) => void;
  handleItemDragOver: (e: React.DragEvent, index: number) => void;
  handleItemDragEnd: () => void;
}

export default function Gallery({
  galleryItems,
  isDragOver,
  setIsDragOver,
  draggedIndex,
  fileInputRef,
  addFiles,
  removeFile,
  clearAll,
  handleDrop,
  handleItemDragStart,
  handleItemDragOver,
  handleItemDragEnd,
}: Step7Props) {
  const totalGallerySize = galleryItems.reduce(
    (s, item) => (item.file ? s + item.file.size : s),
    0,
  );

  return (
    <div className="animate-fade-in">
      <SectionCard
        title="Galerie"
        subtitle="Ajoutez les images et vidéos qui illustrent l'activité. Glissez-déposez ou parcourez vos fichiers. Vous pouvez réorganiser l'ordre par glisser-déposer."
      >
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className="text-center py-5 mb-3"
          style={{
            border: `2px dashed ${isDragOver ? "#f5c518" : "#d8d8d8"}`,
            borderRadius: "14px",
            backgroundColor: isDragOver ? "#f5c518" : "#fafafa",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            className="d-none"
            onChange={(e: any) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div style={{ fontSize: "2rem" }}>📁</div>
          <div className="fw-semibold mt-2">
            Glissez vos fichiers ici, ou cliquez pour parcourir
          </div>
          <div className="small text-muted mt-1">
            Images et vidéos acceptées
          </div>
        </div>

        {galleryItems.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="small text-muted">
                {galleryItems.length} élément(s) ·{" "}
                {formatBytes(totalGallerySize)}
              </span>
              <CButton
                size="sm"
                variant="ghost"
                color="danger"
                onClick={clearAll}
              >
                Tout retirer
              </CButton>
            </div>
            <div
              className="d-grid gap-3"
              style={{
                gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              }}
            >
              {galleryItems.map((item, idx) => (
                <div
                  key={item.id}
                  draggable={item.status !== "uploading"}
                  onDragStart={() => handleItemDragStart(idx)}
                  onDragOver={(e) => handleItemDragOver(e, idx)}
                  onDragEnd={handleItemDragEnd}
                  className="position-relative"
                  style={{
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: `2px solid ${draggedIndex === idx ? "#f5c518" : "#ececec"}`,
                    aspectRatio: "1 / 1",
                    background: "#000",
                    cursor: draggedIndex === idx ? "grabbing" : "grab",
                    opacity:
                      draggedIndex === idx
                        ? 0.7
                        : item.status === "uploading"
                          ? 0.6
                          : 1,
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    className="position-absolute d-flex align-items-center justify-content-center"
                    style={{
                      top: 6,
                      left: 6,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      fontSize: "14px",
                      zIndex: 5,
                      cursor: "grab",
                    }}
                    title="Glisser pour réorganiser"
                  >
                    ☰
                  </div>

                  {item.type === "VIDEO" ? (
                    <video
                      src={item.url}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                      muted
                    />
                  ) : (
                    <img
                      src={item.url}
                      alt={item.file?.name || "gallery-image"}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  )}

                  {idx === 0 && (
                    <span
                      className="position-absolute"
                      style={{
                        top: 6,
                        right: 36,
                        backgroundColor: "#f5c518",
                        color: "#fff",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        padding: "2px 6px",
                        borderRadius: "6px",
                        zIndex: 5,
                      }}
                    >
                      Couverture
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(idx);
                    }}
                    className="position-absolute d-flex align-items-center justify-content-center border-0"
                    style={{
                      top: 6,
                      right: 6,
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      backgroundColor: "rgba(220, 53, 69, 0.8)",
                      color: "#fff",
                      zIndex: 5,
                      cursor: "pointer",
                    }}
                    aria-label="Retirer"
                  >
                    ✕
                  </button>

                  <div
                    className="position-absolute w-100 px-2 py-1 text-truncate small text-white"
                    style={{
                      bottom: 0,
                      background:
                        "linear-gradient(transparent, rgba(0,0,0,0.8))",
                      zIndex: 4,
                    }}
                  >
                    {item.file ? item.file.name : "Existant"}
                  </div>
                  {item.status === "uploading" && (
                    <div
                      className="position-absolute d-flex align-items-center justify-content-center w-100 h-100"
                      style={{ background: "rgba(0,0,0,0.35)", zIndex: 6 }}
                    >
                      <div
                        className="spinner-border spinner-border-sm text-light"
                        role="status"
                      />
                    </div>
                  )}

                  {item.status === "error" && (
                    <div
                      className="position-absolute d-flex align-items-center justify-content-center w-100 h-100 text-white small fw-semibold text-center px-2"
                      style={{ background: "rgba(220,53,69,0.75)", zIndex: 6 }}
                    >
                      Échec
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}
